// The "ledger" list pattern (canvas design 1a, "Auto-load on scroll, with a ceiling"):
// position, progress and a clear end state for a long list that scrolls inside a
// bordered frame. Pure logic lives here so it is testable without a DOM; the
// stateful hook is `use-ledger.ts`, the rendering is `table-ledger.tsx`.

/** The ledger's default row height — the footer's "Rows 191–200" readout is
 *  arithmetic on it, so the two must move together. */
export const LEDGER_ROW_PX = 44;
/** Height of a group heading row (`Table` `groupBy`). */
export const LEDGER_GROUP_PX = 36;

export type LedgerState = 'loading' | 'error' | 'ready';

export interface LedgerProps {
  /** `loading` = no rows yet (skeleton), `error` = the read failed (rows already
   *  on screen, if any, are kept and marked possibly out of date). */
  state: LedgerState;
  /** Rows revealed on screen / rows that exist. */
  loaded: number;
  total: number;
  /** Alarms-style live lists: entries that arrived while the reader was scrolled
   *  down and are held back behind the "N new entries" pill. */
  pending: number;
  /** Auto-load stopped at its ceiling with rows still unrevealed. */
  paused: boolean;
  /** Every row is revealed. */
  ended: boolean;
  /** The SOURCE stops at a fixed number of rows (a server-side LIMIT with no total),
   *  so having revealed them all does not mean the list is complete. The footer and
   *  end row then say "limit reached" instead of "all loaded". */
  limited?: boolean | undefined;
  pageSize: number;
  /** Row height in px (default 44, the canvas's one-line row). A list whose cells
   *  carry two lines passes a taller row — the footer's "Rows 191–200" arithmetic
   *  follows it. */
  rowPx?: number | undefined;
  /** Narrowest the table may get before it scrolls sideways (default 520). A table with
   *  fixed-width columns AND one flexible one needs it: the flexible column gets what
   *  the fixed ones leave, so without a floor it collapses to nothing on a small screen. */
  minWidthPx?: number | undefined;
  /** SERVER-paged lists: more rows exist on the server beyond `total`, so `total` is
   *  a floor ("90 of 90+") and the pause row cannot say how many are left. */
  more?: boolean | undefined;
  /** The next server page is being fetched. */
  fetching?: boolean | undefined;
  /** Optional "earliest …" text for the end-of-list note. */
  earliest?: string | undefined;
  onRetry?: (() => void) | undefined;
  onMore: () => void;
  onShowPending: () => void;
  onEndReached: () => void;
  onAtTop: (atTop: boolean) => void;
  /** A column sort became active (`true`) or was cleared (`false`). Sorting only the
   *  revealed rows would put a row at the top that does not belong there, so an
   *  active sort reveals every row; clearing it starts the paging over. */
  onSort: (active: boolean) => void;
}

export type LedgerStatus = 'loading' | 'fetching' | 'error' | 'paused' | 'done' | 'limited' | 'idle';

/** The footer's one-word state, in the order the states can overrule each other. */
export function ledgerStatus(
  p: Pick<LedgerProps, 'state' | 'paused' | 'ended' | 'limited' | 'fetching'>,
): LedgerStatus {
  if (p.state === 'loading') return 'loading';
  if (p.state === 'error') return 'error';
  if (p.fetching) return 'fetching';
  if (p.paused) return 'paused';
  if (p.ended) return p.limited ? 'limited' : 'done';
  return 'idle';
}

/** The scroll box's position in ROWS: the number of rows wholly above its top edge
 *  and wholly above its bottom edge. The header is OUTSIDE the scroll box, so every
 *  pixel of it is rows — except group heading rows: `groupStarts` are the 0-based
 *  indices of the rows each heading sits ABOVE, `groupPx` their height. The footer's
 *  readout only changes when one of these numbers does, so a scroll handler can skip
 *  re-rendering between row boundaries. */
export function scrollRows(
  scrollTop: number,
  clientHeight: number,
  rowPx = LEDGER_ROW_PX,
  groupStarts: readonly number[] = [],
  groupPx = 0,
) {
  // Bottom edge of row k, counting every heading at or above it.
  const bottom = (k: number) => {
    let g = 0;
    for (const s of groupStarts) if (s <= k) g++;
    return (k + 1) * rowPx + groupPx * g;
  };
  // How many rows end at or above `y` (bottom() only grows, so bisect).
  const rowsAbove = (y: number) => {
    let lo = 0;
    let hi = Math.max(0, Math.floor(y / rowPx) + 1);
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (bottom(mid - 1) <= y) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  };
  return { topRow: rowsAbove(scrollTop), bottomRow: rowsAbove(scrollTop + clientHeight) };
}

/** Which rows (1-based, inclusive) are on screen, for "Rows 191–200". Same
 *  formulas as the canvas; `last` never drops below `first`. */
export function visibleRange(topRow: number, bottomRow: number, loaded: number) {
  if (loaded <= 0) return { first: 0, last: 0 };
  const first = Math.min(loaded, topRow + 1);
  return { first, last: Math.max(first, Math.min(loaded, bottomRow)) };
}

/** The next reveal count when the reader nears the bottom. Auto-load advances one
 *  page at a time and stops AT the ceiling (`cap`); only the "Load more" button
 *  (`onMore`) goes past it. Returns `n` unchanged when there is nothing to do. */
export function revealStep(n: number, added: number, total: number, pageSize: number, cap: number) {
  if (n >= cap || n + added >= total) return n;
  return Math.min(n + pageSize, cap);
}

/** Splits a freshly polled list into the rows the reader already knows and the
 *  ones that arrived since. `known === null` means nothing is held back yet (first
 *  load): every row is shown. */
export function splitFresh<T>(items: T[], known: Set<string> | null, id: (row: T) => string) {
  if (!known) return { shown: items, pending: 0 };
  const shown = items.filter((r) => known.has(id(r)));
  return { shown, pending: items.length - shown.length };
}
